import type { Locale } from "@/lib/i18n";

/**
 * Landing-page copy, both locales.
 *
 * Structured rather than flat keys because the page is mostly repeated shapes — four pipeline
 * steps, four benefit cards, three feature cards, three plans, six risk bullets. A flat
 * `landing.card3.title` namespace would be unreadable and would let a translator lose a card
 * without anything noticing; an array cannot silently lose an element, because the render maps
 * over it and a missing one is visible immediately.
 *
 * ⚠️ PLAN PRICES ARE NOT HERE. They are fetched live from GET /plans/prices at runtime (they are
 * admin-settable and have already changed twice), so only the plan NAME and DURATION are
 * translated. Same rule as the FAQ.
 *
 * Haitian Creole in the official 1979 IPN orthography — see content/ui.ts for the rules. Trading
 * terms stay in English and are glossed.
 */

export type LandingDict = {
  loading: string;
  /** The admin-managed popup card (announcements, migration 0035). */
  announce: {
    close: string;
    countdownLabel: string;
    live: string;
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
  };
  hero: {
    eyebrow: string;
    titleBefore: string;
    titleAccent: string;
    titleAfter: string;
    body: string;
    worksWith: string;
    venuesNote: string;
    ctaCreate: string;
    ctaHave: string;
    badges: string[];
    /** The markets strip (components/MarketsStrip): read live from the masters' pair lists. */
    marketsLabel: string;
    marketsNote: string;
  };
  pipeline: { label: string; steps: { t: string; s: string }[] };
  what: {
    eyebrow: string;
    h2: string;
    p1: string;
    p2: string;
    cards: { t: string; d: string }[];
  };
  features: { t: string; d: string }[];
  pricing: {
    eyebrow: string;
    h2: string;
    sub: string;
    planLabels: Record<string, string>;
    months: string;
    perMaster: string;
    /** The Spot card (2026-10-11): sold per year beside the three copy plans; since 2026-10-23 one
     *  Spot subscription covers one Spot account on each exchange (migration 0102). */
    spotLabel: string;
    perAccount: string;
    /** "Pay 1, get 3": one Spot subscription runs Spot on Bybit, Binance and MEXC. */
    spotBundle: string;
    spotFeatures: string[];
    /** Under every price: the networks the contract is deployed on, drawn as their marks. */
    payOn: string;
    popular: string;
    features: string[];
    cta: string;
    footnote: string;
  };
  risk: {
    h2: string;
    items: { b: string; t: string }[];
    cta: string;
    note: string;
  };
};

const en: LandingDict = {
  loading: "Loading…",
  announce: {
    close: "Close",
    countdownLabel: "Starts in",
    live: "Live now",
    days: "days",
    hours: "hrs",
    minutes: "min",
    seconds: "sec",
  },
  hero: {
    eyebrow: "Autonomous copy-trading · Spot",
    titleBefore: "Mirror three autonomous master engines into ",
    titleAccent: "your own",
    titleAfter: " exchange account.",
    body: "twoke runs three fully-owned ML trading engines, each thinking on its own timeframe. Subscribe to a master and its risk-checked trades are copied to your own Bybit, Binance, MEXC or cTrader account — demo or live, your choice. Non-custodial, with a mandatory stop-loss and take-profit on every copy-trading position. Or let Spot buy the projects you choose on the dips — Bitcoin, Ethereum, any pair your exchange lists, tokenized stocks — on your own account, for the long term.",
    worksWith: "Works with",
    venuesNote: "· demo & live · trade-only keys",
    ctaCreate: "Create account",
    ctaHave: "I already have one",
    badges: [
      "Non-custodial",
      "100% owned ML — no external API",
      "Mandatory SL / TP",
    ],
    marketsLabel: "Markets",
    marketsNote: "what the masters trade today — each tile names the masters that carry it",
  },
  pipeline: {
    label: "How a trade reaches you",
    steps: [
      { t: "Master engine decides", s: "owned ONNX policy — or abstains" },
      {
        t: "Deterministic risk engine",
        s: "never the last step before an order",
      },
      { t: "Sized to the assets in your account", s: "your % risk, your caps" },
      { t: "Placed on your account", s: "with live SL + TP attached" },
    ],
  },
  what: {
    eyebrow: "What is copy trading?",
    h2: "Trade like a master — without watching the charts.",
    p1: "Copy trading automatically mirrors an expert trader's moves into your own account. When a twoke master enters, adjusts, or exits a position, the same action is scaled to the assets in your account and placed on your Bybit, Binance or cTrader account — hands-free. You keep full control: your keys, your funds, your risk limits, and a kill switch you can hit anytime.",
    p2: "Unlike opaque signal groups, every copied trade is attributed and transparent — you can see which master's decision produced each position. Subscribe per master, cancel the copy anytime.",
    cards: [
      {
        t: "Automatic",
        d: "Master trades are copied in real time — no manual order entry, no screen time.",
      },
      {
        t: "Scaled to you",
        d: "Fixed-fractional, exact-mirror or fixed-lot — sized from the assets in your account, read live, capped by your risk %.",
      },
      {
        t: "Transparent",
        d: "Each position traces back to the exact master decision that produced it.",
      },
      {
        t: "You stay in control",
        d: "Your own trade-only key (no withdrawal), per-master enable/disable, and a kill switch.",
      },
    ],
  },

  features: [
    {
      t: "Three masters",
      d: "Each a separately-trained, fully-owned model.",
    },
    {
      t: "Non-custodial by design",
      d: "Connect your own trade-only exchange key (no withdrawal). We never hold your crypto.",
    },
    {
      t: "Owned decision brain",
      d: "The trading policy is a self-hosted model trained on the master's own lessons — no external LLM/API.",
    },
    {
      t: "Spot — buy the dips",
      d: "Pick your projects, a budget per pair and a method — Fibonacci, RSI or % down. Spot buys lower levels on your own Bybit, Binance or MEXC account, and sells only if you switch on take profit or a stop.",
    },
  ],
  pricing: {
    eyebrow: "Pricing",
    h2: "Per master. No monthly, no hidden fees.",
    sub: "One subscription per master for copy-trading; one Spot subscription covers one Spot account on each exchange (Bybit, Binance, MEXC) — paid in USDC, USDT or the network's own coin. Prices below are live.",
    planLabels: {
      quarterly: "Quarterly",
      semi_annual: "Semi-annual",
      annual: "Annual",
    },
    months: "months",
    perMaster: "per master",
    spotLabel: "Spot · yearly",
    perAccount: "Bybit, Binance and MEXC",
    spotBundle: "Pay 1, get 3",
    spotFeatures: [
      "Buys the projects you choose on the dips",
      "Fibonacci, RSI or % down — your method per pair",
      "Any pair your exchange lists",
      "Your own Bybit, Binance or MEXC account, live",
    ],
    payOn: "Pay in",
    popular: "popular",
    features: [
      "All of that master's live signals",
      "Auto-copied, risk-checked trades",
      "Mandatory SL/TP + your risk caps",
      "Bybit, Binance, MEXC or cTrader · demo or live",
    ],
    cta: "Get started",
    footnote:
      "Upgrade anytime — remaining time is preserved. Renewals extend from your current expiry. No refunds.",
  },
  risk: {
    h2: "Trading risk disclosure & agreement",
    items: [
      {
        b: "Substantial risk of loss.",
        t: "Trading crypto-assets, derivatives and CFDs involves significant risk and is not suitable for everyone. You can lose some or all of your crypto.",
      },
      {
        b: "Copy-trading, not advice.",
        t: "Trades mirror a master's decisions to your account. Past performance is not indicative of future results. twoke provides tools, not financial advice.",
      },
      {
        b: "Stops reduce, not remove, risk.",
        t: "Every copy-trading position gets a mandatory stop-loss and take-profit (Spot buys have none), but gaps, slippage and liquidation can still cause losses.",
      },
      {
        b: "Non-custodial & self-directed.",
        t: "twoke never holds, moves or withdraws your crypto-assets or other funds: they stay in your own exchange account and your own wallet. You connect your own trade-only key and choose demo or live. Live trading uses real funds entirely at your own risk.",
      },
      {
        b: "No refunds.",
        t: "Subscriptions are non-refundable — not partial, not pro-rated. This is confirmed again at the point of payment.",
      },
      {
        b: "Your responsibility.",
        t: "You are solely responsible for choosing to subscribe, for your settings, and for complying with the laws and regulations of your jurisdiction.",
      },
      {
        b: "Automated, and your results differ.",
        t: "Software places trades on your account without your intervention, and the masters are automated engines that can be wrong. Your fills, skipped copies and sizes mean your results will differ from the master's.",
      },
      {
        b: "No bank, no BRH licence.",
        t: "twoke is established in Haiti but is not a bank or financial institution under the Haitian law of 14 May 2012, and is not licensed or supervised by the BRH. No deposit guarantee covers your funds, and Haiti has no law that licenses or forbids crypto-assets.",
      },
      {
        b: "Not every risk is listed.",
        t: "These are the main risks, not all of them: trading and crypto-assets carry risks no one can foresee. Read the full Terms before you subscribe.",
      },
    ],
    cta: "Review & agree to get started",
    note: "You'll confirm this disclosure when you create your account, and accept the full Terms of Service in-app before subscribing to a master or connecting an exchange key.",
  },
};

const ht: LandingDict = {
  loading: "L ap chaje…",
  announce: {
    close: "Fèmen",
    countdownLabel: "Kòmanse nan",
    live: "Ap pase kounye a",
    days: "jou",
    hours: "è",
    minutes: "min",
    seconds: "segonn",
  },
  hero: {
    eyebrow: "Copy trading otonòm · Spot",
    titleBefore: "Kopye twa motè mèt otonòm nan ",
    titleAccent: "pwòp kont",
    titleAfter: " echanj pa w.",
    body: "twoke fè mache twa motè trading ML li posede nèt, chak k ap reflechi sou pwòp timeframe pa l. Abòne ak yon mèt epi trade li yo, ki pase nan kontwòl risk, kopye nan pwòp kont Bybit, Binance, MEXC oswa cTrader ou — demo oswa reyèl, se ou ki chwazi. twoke pa janm kenbe kripto ou, epi gen yon stop-loss ak yon take-profit obligatwa sou chak pozisyon copy trading. Oswa kite Spot achte pwojè ou chwazi yo lè pri a bese — Bitcoin, Ethereum, nenpòt pè echanj ou a genyen, aksyon tokenize — sou pwòp kont ou, pou lontan.",
    worksWith: "Li mache ak",
    venuesNote: "· demo ak reyèl · kle pou trading sèlman",
    ctaCreate: "Kreye yon kont",
    ctaHave: "Mwen deja gen youn",
    badges: [
      "Pa janm kenbe kripto ou",
      "100% ML nou posede — pa gen API deyò",
      "SL / TP obligatwa",
    ],
    marketsLabel: "Mache yo",
    marketsNote: "sa mèt yo ap trade jodi a — chak kare di ki mèt k ap pote l",
  },
  pipeline: {
    label: "Kijan yon trade rive jwenn ou",
    steps: [
      { t: "Motè mèt la deside", s: "règ ONNX nou posede — oswa li absteni" },
      {
        t: "Motè risk deterministik",
        s: "li pa janm dènye etap anvan yon lòd",
      },
      { t: "Kalkile sou aktif ki nan kont ou", s: "pousantaj risk pa w, limit pa w" },
      { t: "Plase sou kont ou", s: "ak SL + TP aktif tache" },
    ],
  },
  what: {
    eyebrow: "Kisa copy trading ye?",
    h2: "Fè trade tankou yon mèt — san ou pa gade graf yo.",
    p1: "Copy trading kopye mouvman yon trader ki gen eksperyans nan pwòp kont ou otomatikman. Lè yon mèt twoke antre, ajiste oswa soti nan yon pozisyon, menm aksyon an kalkile dapre aktif ki nan kont ou epi plase sou kont Bybit, Binance oswa cTrader ou — san ou pa manyen anyen. Ou kenbe kontwòl nèt: kle pa w, kripto pa w, limit risk pa w, ak yon kill switch ou ka peze nenpòt kilè.",
    p2: "Kontrèman ak gwoup siyal ki pa klè, chak trade kopye gen sous li epi li transparan — ou ka wè ki desizyon mèt ki pwodwi chak pozisyon. Abòne pou chak mèt, kanpe kopi a nenpòt kilè.",
    cards: [
      {
        t: "Otomatik",
        d: "Yo kopye trade mèt la an tan reyèl — ou pa antre okenn lòd alamen, ou pa bezwen gade ekran.",
      },
      {
        t: "Kalkile pou ou",
        d: "Fraksyon fiks, mirwa egzak oswa kantite fiks — kalkile sou aktif ki nan kont ou jan yo ye kounye a, limite pa pousantaj risk ou.",
      },
      {
        t: "Transparan",
        d: "Chak pozisyon remonte jouk nan desizyon mèt la ki pwodwi l la.",
      },
      {
        t: "Ou rete an kontwòl",
        d: "Pwòp kle w ki pou trading sèlman (san retrè), aktive/dezaktive pou chak mèt, ak yon kill switch.",
      },
    ],
  },

  features: [
    {
      t: "Twa mèt",
      d: "Chak se yon modèl apa, antrene apa, nou posede nèt.",
    },
    {
      t: "Kripto ou rete nan kont ou",
      d: "Konekte pwòp kle echanj ou ki pou trading sèlman (san retrè). Nou pa janm kenbe kripto ou.",
    },
    {
      t: "Sèvo desizyon nou posede",
      d: "Règ trading la se yon modèl nou menm ki fè l mache, antrene sou pwòp leson mèt la — pa gen LLM ni API deyò.",
    },
    {
      t: "Spot — achte lè pri a bese",
      d: "Chwazi pwojè ou yo, yon bidjè pou chak pè ak yon metòd — Fibonacci, RSI oswa % desann. Spot achte nan nivo ki pi ba sou pwòp kont Bybit, Binance oswa MEXC ou, epi li vann sèlman si ou limen take profit oswa yon stop.",
    },
  ],
  pricing: {
    eyebrow: "Pri",
    h2: "Pou chak mèt. Pa gen plan chak mwa, pa gen frè kache.",
    sub: "Yon abònman pou chak mèt pou copy trading; yon sèl abònman Spot kouvri yon kont Spot sou chak echanj (Bybit, Binance, MEXC) — peye an USDC, USDT oswa kripto rezo a. Pri anba yo se pri aktyèl la.",
    planLabels: {
      quarterly: "Trimèstriyèl",
      semi_annual: "Sis mwa",
      annual: "Ane",
    },
    months: "mwa",
    perMaster: "pou chak mèt",
    spotLabel: "Spot · chak ane",
    perAccount: "Bybit, Binance ak MEXC",
    spotBundle: "Peye 1, jwenn 3",
    spotFeatures: [
      "Achte pwojè ou chwazi yo lè pri a bese",
      "Fibonacci, RSI oswa % desann — metòd pa w pou chak pè",
      "Nenpòt pè echanj ou a genyen",
      "Pwòp kont Bybit, Binance oswa MEXC ou, reyèl",
    ],
    payOn: "Peye an",
    popular: "popilè",
    features: [
      "Tout siyal an dirèk mèt sa a",
      "Trade kopye otomatikman, ki pase nan kontwòl risk",
      "SL/TP obligatwa + limit risk pa w",
      "Bybit, Binance, MEXC oswa cTrader · demo oswa reyèl",
    ],
    cta: "Kòmanse",
    footnote:
      "Monte plan nenpòt kilè — nou konsève tan ki rete w. Renouvèlman pwolonje apati ekspirasyon aktyèl ou. Pa gen ranbousman.",
  },
  risk: {
    h2: "Deklarasyon ak akò sou risk trading",
    items: [
      {
        b: "Gwo risk pou pèdi kripto.",
        t: "Trading sou aktif kripto, pwodwi derive ak CFD pote yon gwo risk epi li pa bon pou tout moun. Ou ka pèdi yon pati oswa tout kripto ou.",
      },
      {
        b: "Copy trading, se pa konsèy.",
        t: "Trade yo kopye desizyon yon mèt nan kont ou. Rezilta ki pase pa endike rezilta k ap vini. twoke bay zouti, li pa bay konsèy finansye.",
      },
      {
        b: "Stop diminye risk, li pa retire l.",
        t: "Chak pozisyon copy trading jwenn yon stop-loss ak yon take-profit obligatwa (acha Spot yo pa genyen), men gap, slippage ak likidasyon ka toujou lakòz pèt.",
      },
      {
        b: "Kripto ou rete pa w, epi se ou ki dirije.",
        t: "twoke pa janm kenbe, deplase ni retire kripto ou: li rete nan pwòp kont echanj ou ak nan pwòp pòtfèy kripto ou. Ou konekte pwòp kle w ki pou trading sèlman epi ou chwazi demo oswa reyèl. Trading reyèl itilize aktif reyèl antyèman sou pwòp risk ou.",
      },
      {
        b: "Pa gen ranbousman.",
        t: "Abònman pa ranbousab — ni an pati, ni pworata. Nou konfime sa ankò nan moman peman an.",
      },
      {
        b: "Responsablite w.",
        t: "Se ou sèl ki responsab chwa ou fè pou w abòne, paramèt ou yo, ak respè lwa ak règleman jiridiksyon w.",
      },
      {
        b: "Otomatik, epi rezilta w diferan.",
        t: "Yon lojisyèl plase trade sou kont ou san ou pa entèvni, epi mèt yo se motè otomatik ki ka fè erè. Pri kopi yo ranpli, kopi ki sote ak gwosè yo fè rezilta w diferan de rezilta mèt la.",
      },
      {
        b: "Se pa yon bank, li pa gen lisans BRH.",
        t: "twoke etabli ann Ayiti, men li pa yon bank ni yon enstitisyon finansye dapre lwa 14 me 2012 la sou bank ak lòt enstitisyon finansye, epi BRH pa ba li lisans ni li pa sipèvize l. Okenn garanti depo pa kouvri aktif ou yo, epi Ayiti pa gen okenn lwa ki bay lisans pou aktif kripto ni ki entèdi yo.",
      },
      {
        b: "Se pa tout risk ki la a.",
        t: "Sa yo se risk prensipal yo, se pa tout: trading ak aktif kripto pote risk pèsonn pa ka prevwa. Li tout Kondisyon Sèvis yo anvan ou abòne.",
      },
    ],
    cta: "Li epi dakò pou w kòmanse",
    note: "W ap konfime deklarasyon sa a lè ou kreye kont ou, epi w ap aksepte tout Kondisyon Sèvis yo nan aplikasyon an anvan ou abòne ak yon mèt oswa konekte yon kle echanj.",
  },
};

const DICTS: Record<Locale, LandingDict> = { en, ht };

export const getLanding = (locale: Locale): LandingDict => DICTS[locale] ?? en;
